import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAtomValue, useSetAtom } from 'jotai';
import {
  selectedConversationIdAtom,
  selectedConversationAtom,
  deleteConversationAtom,
} from '../../../../atoms/v3/chatAtoms';
import { layoutModeAtom, showPersonalizationAtom } from '../../../../atoms/v3/uiAtoms';
import { useToast } from '@chakra-ui/react';
import { IOSMoreButton } from '../ios/IOSMoreButton';
import { IOSActionSheet } from '../ios/IOSActionSheet';
import { navigateAtom } from '../../../../atoms/v3/navigationAtoms';



/**
 * 聊天更多选项组件
 * 包含删除对话等操作，使用 iOS 风格的 ActionSheet
 */
export const ChatMoreOptions: React.FC = () => {
  const { t } = useTranslation(['v3/chat', 'common']);
  const [isActionSheetOpen, setIsActionSheetOpen] = useState(false);
  const selectedConversationId = useAtomValue(selectedConversationIdAtom);
  const selectedConversation = useAtomValue(selectedConversationAtom);
  const deleteConversation = useSetAtom(deleteConversationAtom);
  const toast = useToast();

  const navigate = useSetAtom(navigateAtom);
  const layoutMode = useAtomValue(layoutModeAtom);
  const setShowPersonalization = useSetAtom(showPersonalizationAtom);
  

  const handleDeleteConversation = async () => {
    if (!selectedConversationId) return;
    if (window.confirm(t('v3/chat:confirmations.deleteChat'))) {
      try {
        const success = await deleteConversation(selectedConversationId);
        if (success) {
          toast({
            title: t('v3/chat:toast.deleteSuccess'),
            status: 'success',
            duration: 2000,
            isClosable: true,
            position: 'top'
          });
        } else {
          toast({
            title: t('v3/chat:toast.deleteFailed'),
            description: t('common:errors.tryAgain'),
            status: 'error',
            duration: 3000,
            isClosable: true,
            position: 'top'
          });
        }
      } catch (err) {
        console.error('Failed to delete conversation:', err);
        toast({
          title: t('v3/chat:toast.deleteFailed'),
          description: t('common:errors.checkNetworkAndRetry'),
          status: 'error',
          duration: 3000,
          isClosable: true,
          position: 'top'
        });
      }
    }
  };

  // 打开个性化设置页面
  const handleOpenPersonalizationSettings = () => {
    // 在桌面模式下，使用状态管理而不是路由
    if (layoutMode === 'desktop') {
      setShowPersonalization(true);
    } else {
      // 移动模式下使用路由
      navigate({ path: '/v3/chat-personalization-settings' });
    }
  };

  // 准备 ActionSheet 的 items
  const actionSheetItems = [
    {
      id: 'settings',
      label: t('v3/chat:actions.chatSettings'),
      onClick: handleOpenPersonalizationSettings,
      icon: (
        <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M3 6H5H21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M8 6V4C8 3.46957 8.21071 2.96086 8.58579 2.58579C8.96086 2.21071 9.46957 2 10 2H14C14.5304 2 15.0391 2.21071 15.4142 2.58579C15.7893 2.96086 16 3.46957 16 4V6M19 6V20C19 20.5304 18.7893 21.0391 18.4142 21.4142C18.0391 21.7893 17.5304 22 17 22H7C6.46957 22 5.96086 21.7893 5.58579 21.4142C5.21071 21.0391 5 20.5304 5 20V6H19Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      )
    },
    selectedConversationId && {
      id: 'delete',
      label: t('v3/chat:actions.deleteConversation'),
      destructive: true,
      onClick: handleDeleteConversation,
      icon: (
        <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M3 6H5H21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M8 6V4C8 3.46957 8.21071 2.96086 8.58579 2.58579C8.96086 2.21071 9.46957 2 10 2H14C14.5304 2 15.0391 2.21071 15.4142 2.58579C15.7893 2.96086 16 3.46957 16 4V6M19 6V20C19 20.5304 18.7893 21.0391 18.4142 21.4142C18.0391 21.7893 17.5304 22 17 22H7C6.46957 22 5.96086 21.7893 5.58579 21.4142C5.21071 21.0391 5 20.5304 5 20V6H19Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      )
    }
  ].filter(Boolean);

  return (
    <>
      <IOSMoreButton
        onClick={() => setIsActionSheetOpen(true)}
      />

      <IOSActionSheet
        isOpen={isActionSheetOpen}
        onClose={() => setIsActionSheetOpen(false)}
        title={selectedConversation?.title || '新对话'}
        items={actionSheetItems}
      />
    </>
  );
};
